import { useRef } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useFormik } from 'formik';
import { signInValidationSchema } from '../../schemas';
import FinvuLogo from '../../assets/img/finfactor_svg_logo.svg';
import { useDispatch } from 'react-redux';
import { authActions } from '../../store/reducers/authSlice';
import styles from './SignIn.module.css';
import Error from '../../components/Error/Error';
import CommonApi from '../../services/common.service';
import { toast } from 'react-toastify';
import jwtDecode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { generateFIUConfigDetails, getFipList } from '../../utils/common.utils';
import { uiActions } from '../../store/reducers/uiSlice';
import Turnstile from '../../components/Turnstile/Turnstile';
import { DISABLE_CAPTCHA, HIDE_CREATE_CONSENT } from '../../utils/features';

const initialValues = {
	username: '',
	password: '',
	captchaToken: '',
};

const SignIn = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const api = new CommonApi();
	const turnstileRef = useRef(null);

	const updateFips = (fips) => {
		dispatch(uiActions.updateFips(fips));
	};

	const handleSignIn = (values) => {
		const currentTS = new Date().toISOString();
		const data = {
			header: {
				rid: '42c06b9f-cc5b-4a53-9119-9ca9d8e9acdb',
				ts :currentTS,
				channelId: 'finsense',
			},
			body: {
				userId: values.username,
				password: values.password,
				...(!DISABLE_CAPTCHA && { captchaToken: values.captchaToken }),
			},
		};
		api
			.signIn(data)
			.then((res) => {
				// console.log(res);
				dispatch(authActions.login());
				// document.cookie = `token=${res.data?.body?.token}`;
				sessionStorage.setItem(
					'login',
					JSON.stringify({
						login: true,
						token: res.data?.body?.token,
						authUser: res.data?.header,
					})
				);
				const decodedToken = jwtDecode(res.data?.body?.token);
				const fiuId = decodedToken?.tid;
				const role = decodedToken?.roles;
				sessionStorage.setItem('fiuId', fiuId);
				generateFIUConfigDetails();
				if (fiuId.includes('axis')) {
					dispatch(authActions.setPFMFlag(true));
				}
				if (role !== 'admin') {
					navigate(
						HIDE_CREATE_CONSENT
							? '/track-consent-request'
							: '/create-consent-request'
					);
				}
				getFipList(updateFips);
			})
			.catch((err) => {
				// Token is single-use; get a fresh one for the retry.
				turnstileRef.current?.reset();

				// Show the server's message, e.g. a captcha failure.
				const serverMessage =
					err?.response?.data?.errors?.[0]?.errorMsg ||
					err?.response?.data?.message;
				toast.error(serverMessage || 'Invalid credentials!');
			});
	};

	const {
		values,
		errors,
		touched,
		handleBlur,
		handleChange,
		handleSubmit,
		setFieldValue,
	} = useFormik({
		initialValues: initialValues,
		validationSchema: signInValidationSchema,
		onSubmit: (values, action) => {
			handleSignIn(values);
			action.setSubmitting(false);
		},
	});

	return (
		<div className={styles['sign-in__container']}>
			<div>
				<img
					src={FinvuLogo}
					alt="Finvu logo"
					height="110"
					width="220"
					className={styles.logo}
				/>
				<Card title="Sign In" className={styles['sign-in__card']}>
					<Divider />
					<form
						onSubmit={handleSubmit}
						className={styles['sign-in-form__container']}
					>
						<div className="form-control">
							<InputText
								placeholder="Your username"
								type="text"
								className={styles.input}
								name="username"
								value={values.username}
								onChange={handleChange}
								onBlur={handleBlur}
							/>
							{touched.username && errors.username ? (
								<Error>{errors.username}</Error>
							) : null}
						</div>
						<div>
							<InputText
								placeholder="Your Password"
								type="password"
								className={styles.input}
								name="password"
								value={values.password}
								onChange={handleChange}
								onBlur={handleBlur}
							/>
							{touched.password && errors.password ? (
								<Error>{errors.password}</Error>
							) : null}
						</div>
						{!DISABLE_CAPTCHA && (
							<Turnstile
								ref={turnstileRef}
								onToken={(token) => setFieldValue('captchaToken', token)}
								error={errors.captchaToken}
								touched={touched.captchaToken}
							/>
						)}
						<Button className={styles['sign-in__button']}>Sign In</Button>
						{/* <Link className={styles['forgot-password']}>Forgot Password?</Link> */}
					</form>
				</Card>
			</div>
		</div>
	);
};

export default SignIn;
